import { useEffect, useMemo, useRef, useState } from "react"
import type { inferRouterOutputs } from "@trpc/server"
import { CheckCircle2, CircleAlert, Clock3, Copy, ExternalLink, Eye, FileText, Pencil, RefreshCw } from "lucide-react"
import toast from "react-hot-toast"
import { useHotkeys } from "react-hotkeys-hook"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"

import type { AppRouter } from "../../../aixinternal-api/src/router/_app"
import { useDebouncedValue } from "../lib/filters/useUrlFilters"
import { trpc } from "../lib/trpc"
import { healthBadgeVariant, SEO_PAGE_HEALTHS, SEO_PAGE_STATES, stateBadgeVariant } from "./seoBoardHelpers"
import SEOPageEditor from "./SEOPageEditor"
import SEORevisionHistory from "./SEORevisionHistory"
import { buildSeoPageReviewSummary, presentSeoFindings } from "./seoPageReviewModel"

type RouterOutputs = inferRouterOutputs<AppRouter>
type PageDetail = RouterOutputs["seo"]["pages"]["get"]
type PagePreview = RouterOutputs["seo"]["pages"]["preview"]
type PageDelivery = RouterOutputs["seo"]["pages"]["delivery"]

const ANY = "__any__"
const PAGE_SIZE = 100

interface SEOPagesTabProps {
  productKey: string
  productDomain: string | null | undefined
  selectedSlug: string
  mode: "inspect" | "preview" | "edit"
  onSelectedSlugChange: (slug: string) => void
  onModeChange: (mode: "inspect" | "preview" | "edit") => void
  onDirtyChange: (dirty: boolean) => void
  shortcutGuideOpen: boolean
}

export default function SEOPagesTab({
  productKey,
  productDomain,
  selectedSlug,
  mode,
  onSelectedSlugChange,
  onModeChange,
  onDirtyChange,
  shortcutGuideOpen,
}: SEOPagesTabProps) {
  const [stateFilter, setStateFilter] = useState<string>(ANY)
  const [healthFilter, setHealthFilter] = useState<string>(ANY)
  const [clusterFilter, setClusterFilter] = useState<string>(ANY)
  const [searchQuery, setSearchQuery] = useState("")
  const [pageOffset, setPageOffset] = useState(0)
  const [pruneTarget, setPruneTarget] = useState<string | null>(null)
  const [editorDirty, setEditorDirty] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<{
    slug: string
    mode: "inspect" | "preview" | "edit"
  } | null>(null)
  const [pendingPageEdge, setPendingPageEdge] = useState<"first" | "last" | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const selectedRowRef = useRef<HTMLTableRowElement>(null)

  const utils = trpc.useUtils()
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim(), 250)

  const listInput = {
    productKey,
    state: stateFilter === ANY ? undefined : stateFilter,
    health: healthFilter === ANY ? undefined : healthFilter,
    cluster: clusterFilter === ANY ? undefined : clusterFilter,
    query: debouncedSearchQuery || undefined,
    limit: PAGE_SIZE,
    offset: pageOffset,
  }
  const { data, isLoading, error } = trpc.seo.pages.list.useQuery(listInput)
  const rows = useMemo(() => data?.rows ?? [], [data?.rows])

  const clusters = [...new Set(rows.map((r) => r.cluster).filter((c): c is string => !!c))].sort()

  const detail = trpc.seo.pages.get.useQuery({ productKey, slug: selectedSlug }, { enabled: Boolean(selectedSlug) })
  const preview = trpc.seo.pages.preview.useQuery(
    { productKey, slug: selectedSlug },
    { enabled: Boolean(selectedSlug) && mode === "preview" },
  )
  const delivery = trpc.seo.pages.delivery.useQuery(
    { productKey, slug: selectedSlug },
    { enabled: Boolean(selectedSlug), retry: false },
  )

  const selectedIndex = rows.findIndex((row) => row.slug === selectedSlug)

  const updateEditorDirty = (dirty: boolean) => {
    setEditorDirty(dirty)
    onDirtyChange(dirty)
  }

  const navigate = (slug: string, nextMode: "inspect" | "preview" | "edit") => {
    if (editorDirty && (slug !== selectedSlug || nextMode !== "edit")) {
      setPendingNavigation({ slug, mode: nextMode })
      return
    }
    onSelectedSlugChange(slug)
    onModeChange(nextMode)
  }

  useEffect(() => {
    if (editorDirty || isLoading || rows.length === 0) return
    if (pendingPageEdge) {
      const nextRow = pendingPageEdge === "first" ? rows[0] : rows.at(-1)
      if (nextRow) onSelectedSlugChange(nextRow.slug)
      setPendingPageEdge(null)
      return
    }
    if (selectedIndex < 0) onSelectedSlugChange(rows[0].slug)
  }, [editorDirty, isLoading, onSelectedSlugChange, pendingPageEdge, rows, selectedIndex])

  useEffect(() => {
    setPendingPageEdge(null)
    setPageOffset(0)
  }, [clusterFilter, debouncedSearchQuery, healthFilter, productKey, stateFilter])

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest" })
  }, [selectedSlug])

  const moveSelection = (direction: 1 | -1) => {
    if (rows.length === 0) return
    if (editorDirty) {
      toast.error("Save or discard the current edit before moving to another page")
      return
    }
    if (direction === 1 && selectedIndex === rows.length - 1 && data?.hasMore) {
      setPendingPageEdge("first")
      onModeChange("inspect")
      setPageOffset(data.nextOffset ?? pageOffset + PAGE_SIZE)
      return
    }
    if (direction === -1 && selectedIndex <= 0 && pageOffset > 0) {
      setPendingPageEdge("last")
      onModeChange("inspect")
      setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))
      return
    }
    const nextIndex = selectedIndex < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, selectedIndex + direction))
    navigate(rows[nextIndex].slug, "inspect")
  }

  const clearFilters = () => {
    setStateFilter(ANY)
    setHealthFilter(ANY)
    setClusterFilter(ANY)
    setSearchQuery("")
    setPageOffset(0)
    toast.success("Page filters cleared")
  }

  useHotkeys("j, down", () => moveSelection(1), { preventDefault: true, enableOnFormTags: false }, [
    rows,
    selectedIndex,
    data?.hasMore,
    data?.nextOffset,
    editorDirty,
    pageOffset,
  ])
  useHotkeys("k, up", () => moveSelection(-1), { preventDefault: true, enableOnFormTags: false }, [
    rows,
    selectedIndex,
    data?.hasMore,
    data?.nextOffset,
    editorDirty,
    pageOffset,
  ])
  useHotkeys(
    "g",
    () => rows[0] && navigate(rows[0].slug, "inspect"),
    { preventDefault: true, enableOnFormTags: false },
    [editorDirty, rows, selectedSlug],
  )
  useHotkeys(
    "shift+g",
    () => {
      const lastRow = rows.at(-1)
      if (lastRow) navigate(lastRow.slug, "inspect")
    },
    { preventDefault: true, enableOnFormTags: false },
    [editorDirty, rows, selectedSlug],
  )
  useHotkeys(
    "enter, p",
    () => {
      if (selectedSlug) navigate(selectedSlug, "preview")
    },
    { preventDefault: true, enableOnFormTags: false },
    [editorDirty, selectedSlug],
  )
  useHotkeys(
    "e",
    () => {
      if (selectedSlug) navigate(selectedSlug, "edit")
    },
    { preventDefault: true, enableOnFormTags: false },
    [editorDirty, selectedSlug],
  )
  useHotkeys("backslash", clearFilters, { preventDefault: true, enableOnFormTags: false })
  useHotkeys("/", () => searchInputRef.current?.focus(), { preventDefault: true, enableOnFormTags: false }, [])
  useHotkeys(
    "esc",
    () => {
      if (shortcutGuideOpen) return
      if (document.activeElement === searchInputRef.current) {
        searchInputRef.current?.blur()
        return
      }
      if (mode !== "inspect") navigate(selectedSlug, "inspect")
    },
    { enableOnFormTags: true },
    [editorDirty, mode, selectedSlug, shortcutGuideOpen],
  )

  const invalidate = () => {
    void utils.seo.pages.list.invalidate()
    if (selectedSlug) {
      void utils.seo.pages.get.invalidate({ productKey, slug: selectedSlug })
      void utils.seo.pages.preview.invalidate({ productKey, slug: selectedSlug })
      void utils.seo.pages.delivery.invalidate({ productKey, slug: selectedSlug })
    }
  }

  const gateMutation = trpc.seo.pages.gate.useMutation({
    onError: (e) => toast.error(e.message || "Gate failed"),
    onSuccess: (res) => {
      const first = res.results[0]
      if (first && !first.passed) toast.error(`Gate failed: ${first.issues.slice(0, 2).join("; ")}`)
      else toast.success(`Gated ${res.passed}/${res.total}`)
      invalidate()
    },
  })

  const pruneMutation = trpc.seo.pages.prune.useMutation({
    onError: (e) => toast.error(e.message || "Prune failed"),
    onSuccess: (res) => {
      toast.success(`Pruned ${res.pruned} page(s)`)
      invalidate()
    },
  })

  const restoreMutation = trpc.seo.pages.restore.useMutation({
    onError: (e) => toast.error(e.message || "Restore failed"),
    onSuccess: (res) => {
      toast.success(`Restored ${res.restored} page(s) to draft`)
      invalidate()
    },
  })

  const page = detail.data
  const content = page?.content

  return (
    <div className="flex h-full flex-col">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-border bg-background px-3 py-2">
        <Input
          ref={searchInputRef}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          aria-label="Search SEO pages by path"
          placeholder="Find page by path… ( / )"
          className="h-8 w-56 text-xs"
        />
        <span className="sr-only" aria-live="polite">
          {selectedSlug ? `Active SEO page: ${selectedSlug}. Mode: ${mode}.` : "No active SEO page"}
        </span>
        <FilterSelect label="State" value={stateFilter} onChange={setStateFilter} options={[...SEO_PAGE_STATES]} />
        <FilterSelect label="Health" value={healthFilter} onChange={setHealthFilter} options={[...SEO_PAGE_HEALTHS]} />
        <FilterSelect label="Cluster" value={clusterFilter} onChange={setClusterFilter} options={clusters} />
        <Button variant="outline" size="sm" onClick={clearFilters}>
          Clear
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {rows.length ? `${pageOffset + 1}–${pageOffset + rows.length}` : "0"} page(s)
        </span>
      </div>

      <ResizablePanelGroup direction="horizontal" autoSaveId="seo-pages-panes" className="flex-1">
        {/* Table */}
        <ResizablePanel defaultSize={62} minSize={35} className="flex flex-col">
          <div className="min-h-0 flex-1 overflow-auto">
            {error ? (
              <ErrorState message={error.message} />
            ) : isLoading ? (
              <LoadingState />
            ) : rows.length === 0 ? (
              <EmptyState message="No pages match these filters." />
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-secondary text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Slug</th>
                    <th className="px-3 py-2">Cluster</th>
                    <th className="px-3 py-2">State</th>
                    <th className="px-3 py-2">Health</th>
                    <th className="px-3 py-2">Gate</th>
                    <th className="px-3 py-2">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      ref={selectedSlug === r.slug ? selectedRowRef : undefined}
                      role="button"
                      tabIndex={selectedSlug === r.slug ? 0 : -1}
                      aria-current={selectedSlug === r.slug ? "true" : undefined}
                      aria-label={`Inspect SEO page ${r.slug}`}
                      onClick={() => {
                        navigate(r.slug, "inspect")
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return
                        event.preventDefault()
                        event.stopPropagation()
                        navigate(r.slug, "inspect")
                      }}
                      className={`cursor-pointer border-b border-border hover:bg-secondary ${
                        selectedSlug === r.slug
                          ? "bg-secondary outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                          : ""
                      }`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{r.slug}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.cluster ?? "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant={stateBadgeVariant(r.state)}>{r.state}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={healthBadgeVariant(r.health)}>{r.health}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        {r.gatePassed === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Badge variant={r.gatePassed ? "success" : "destructive"}>
                            {r.gatePassed ? "pass" : "fail"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border p-2">
            <Button
              size="sm"
              variant="outline"
              disabled={pageOffset === 0}
              onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <span className="text-[11px] text-muted-foreground">Page {Math.floor(pageOffset / PAGE_SIZE) + 1}</span>
            <Button
              size="sm"
              variant="outline"
              disabled={!data?.hasMore}
              onClick={() => setPageOffset(data?.nextOffset ?? pageOffset)}
            >
              Next
            </Button>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Preview */}
        <ResizablePanel defaultSize={38} minSize={28} maxSize={60}>
          <div className="h-full overflow-auto bg-background p-4">
            {!selectedSlug ? (
              <p className="text-sm text-muted-foreground">Select a page to preview its content.</p>
            ) : detail.isLoading ? (
              <LoadingState />
            ) : !page ? (
              <p className="text-sm text-muted-foreground">Page not found.</p>
            ) : mode === "edit" && content ? (
              <SEOPageEditor
                productKey={productKey}
                contentHash={page.contentHash}
                initialContent={content}
                onDirtyChange={updateEditorDirty}
                onCancel={() => navigate(page.slug, "inspect")}
                onSaved={() => {
                  updateEditorDirty(false)
                  invalidate()
                  onModeChange("inspect")
                }}
              />
            ) : mode === "preview" ? (
              <DraftPreview
                page={page}
                productDomain={productDomain}
                previewData={preview.data}
                previewLoading={preview.isLoading}
                previewRefreshing={preview.isFetching}
                previewError={preview.error?.message}
                deliveryData={delivery.data}
                deliveryError={delivery.error?.message}
                onRefresh={() => void preview.refetch()}
                onInspect={() => navigate(page.slug, "inspect")}
              />
            ) : (
              <div className="space-y-4">
                {/* Action bar */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate(page.slug, "preview")}>
                    <Eye className="mr-1.5 h-4 w-4" /> Preview current draft
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(page.slug, "edit")}>
                    <Pencil className="mr-1.5 h-4 w-4" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => gateMutation.mutate({ productKey, slugs: [page.slug] })}
                    loading={gateMutation.isLoading}
                    disabled={page.state === "pruned"}
                  >
                    Gate
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setPruneTarget(page.slug)}
                    disabled={page.state === "pruned"}
                  >
                    Prune
                  </Button>
                  {page.state === "pruned" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreMutation.mutate({ productKey, slugs: [page.slug] })}
                      loading={restoreMutation.isLoading}
                    >
                      Restore to draft
                    </Button>
                  ) : null}
                </div>

                <PreviewTruthLinks
                  slug={page.slug}
                  productDomain={productDomain}
                  deliveryData={delivery.data}
                  deliveryLoading={delivery.isLoading}
                  deliveryError={delivery.error?.message}
                  onPreview={() => navigate(page.slug, "preview")}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={stateBadgeVariant(page.state)}>{page.state}</Badge>
                  <Badge variant={healthBadgeVariant(page.health)}>{page.health}</Badge>
                  {page.cluster ? <Badge variant="outline">{page.cluster}</Badge> : null}
                </div>

                {content?.heroImage?.src ? (
                  <img
                    src={content.heroImage.src}
                    alt={content.heroImage.alt}
                    className="max-h-40 w-full rounded border border-border object-cover"
                  />
                ) : null}

                <Field label="Slug" value={page.slug} mono />
                <Field label="Meta title" value={content?.metaTitle} />
                <Field label="Promise" value={content?.promise} />

                {content?.useCases?.length ? (
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Use cases</p>
                    <ul className="mt-1 list-disc pl-5 text-sm">
                      {[...content.useCases].map((u, i) => (
                        <li key={i}>{typeof u === "string" ? u : ""}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="flex gap-6 text-sm">
                  <span>
                    <span className="text-muted-foreground">FAQ: </span>
                    {content?.faq?.length ?? 0}
                  </span>
                  <span>
                    <span className="text-muted-foreground">Internal links: </span>
                    {content?.internalLinks?.length ?? 0}
                  </span>
                </div>

                {content?.internalLinks?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {content.internalLinks.map((l) => (
                      <Badge key={l} variant="secondary" className="font-mono">
                        {l}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {/* Gate result */}
                <GuidedFindings page={page} onEdit={() => navigate(page.slug, "edit")} />

                <SEORevisionHistory
                  productKey={productKey}
                  slug={page.slug}
                  currentContentHash={page.contentHash}
                  onRestored={() => {
                    updateEditorDirty(false)
                    invalidate()
                  }}
                />
              </div>
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <AlertDialog open={!!pruneTarget} onOpenChange={(o) => !o && setPruneTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prune this page?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{pruneTarget}</span> will be excluded from the next compile/publish. This is
              reversible with Restore to draft, followed by a fresh gate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pruneTarget) pruneMutation.mutate({ productKey, slugs: [pruneTarget] })
                setPruneTarget(null)
              }}
            >
              Prune
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingNavigation} onOpenChange={(open) => !open && setPendingNavigation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved page changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The current editor has changes that have not been saved as a revision. This cannot be recovered from
              revision history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingNavigation) return
                updateEditorDirty(false)
                onSelectedSlugChange(pendingNavigation.slug)
                onModeChange(pendingNavigation.mode)
                setPendingNavigation(null)
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function DraftPreview({
  page,
  productDomain,
  previewData,
  previewLoading,
  previewRefreshing,
  previewError,
  deliveryData,
  deliveryError,
  onRefresh,
  onInspect,
}: {
  page: PageDetail
  productDomain: string | null | undefined
  previewData: PagePreview | undefined
  previewLoading: boolean
  previewRefreshing: boolean
  previewError: string | undefined
  deliveryData: PageDelivery | undefined
  deliveryError: string | undefined
  onRefresh: () => void
  onInspect: () => void
}) {
  const reviewUrl = new URL(window.location.href)
  reviewUrl.searchParams.set("tab", "pages")
  reviewUrl.searchParams.set("page", page.slug)
  reviewUrl.searchParams.set("mode", "preview")

  const copyReviewLink = async () => {
    try {
      await navigator.clipboard.writeText(reviewUrl.toString())
      toast.success("Authenticated draft review link copied")
    } catch {
      toast.error("Could not copy the review link. Use Open review link instead.")
    }
  }

  return (
    <div className="flex h-full min-h-[560px] flex-col">
      <div className="space-y-3 border-b border-border pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Current draft</Badge>
              <span className="font-mono text-[11px] text-muted-foreground">{page.slug}</span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Authenticated preview of the current AIX revision. It is not staged or public.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onInspect}>
              <FileText className="mr-1.5 h-4 w-4" /> Details
            </Button>
            <Button size="sm" variant="outline" onClick={() => void copyReviewLink()}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy review link
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={reviewUrl.toString()} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1.5 h-4 w-4" /> Open review link
              </a>
            </Button>
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={previewRefreshing}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${previewRefreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
        <PreviewTruthLinks
          slug={page.slug}
          productDomain={productDomain}
          deliveryData={deliveryData}
          deliveryLoading={false}
          deliveryError={deliveryError}
          onPreview={() => undefined}
          draftActive
        />
        <DraftReviewChecklist page={page} renderedMediaCount={previewData?.mediaCount ?? 0} />
        {previewData ? (
          <details className="rounded-md border border-border p-2 text-[11px] text-muted-foreground">
            <summary className="cursor-pointer font-medium text-foreground">Technical render details</summary>
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">
              <PreviewMetadata label="Content hash" value={previewData.contentHash} mono />
              <PreviewMetadata label="Template" value={previewData.templateVersion} />
              <PreviewMetadata label="Renderer" value={previewData.rendererVersion} />
              <PreviewMetadata label="Rendered media" value={String(previewData.mediaCount)} />
            </dl>
          </details>
        ) : null}
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-muted/30">
        {previewError ? <ErrorState message={previewError} /> : null}
        {previewLoading ? <LoadingState /> : null}
        {previewData ? (
          <iframe
            title={`Current draft preview for ${page.slug}`}
            srcDoc={previewData.html}
            sandbox="allow-popups"
            className="h-full min-h-[500px] w-full bg-white"
          />
        ) : null}
      </div>
    </div>
  )
}

function DraftReviewChecklist({ page, renderedMediaCount }: { page: PageDetail; renderedMediaCount: number }) {
  const summary = buildSeoPageReviewSummary({
    content: page.content,
    gateResult: page.gateResult,
    renderedMediaCount,
  })

  return (
    <section aria-labelledby="draft-review-checklist-title" className="space-y-3 rounded-md border border-border p-3">
      <div>
        <h3 id="draft-review-checklist-title" className="text-sm font-semibold">
          Page review checklist
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">{summary.nextAction}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {summary.items.map((item) => (
          <div key={item.label} className="rounded bg-muted p-2">
            <div className="flex items-center gap-1.5 text-xs font-medium">
              <ReviewStatusIcon status={item.status} />
              <span>{item.label}</span>
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{item.detail}</p>
          </div>
        ))}
      </div>
      {summary.blockers.length > 0 ? (
        <details className="rounded bg-destructive/10 p-2 text-xs">
          <summary className="cursor-pointer font-medium text-destructive">
            Show {summary.blockers.length} publication blocker{summary.blockers.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
            {summary.blockers.map((blocker, index) => (
              <li key={`${blocker}-${index}`}>{blocker}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}

function ReviewStatusIcon({ status }: { status: "pass" | "fail" | "pending" }) {
  if (status === "pass") {
    return <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Passed" />
  }
  if (status === "fail") {
    return <CircleAlert className="h-4 w-4 text-destructive" aria-label="Blocked" />
  }
  return <Clock3 className="h-4 w-4 text-muted-foreground" aria-label="Pending" />
}

function PreviewTruthLinks({
  slug,
  productDomain,
  deliveryData,
  deliveryLoading,
  deliveryError,
  onPreview,
  draftActive = false,
}: {
  slug: string
  productDomain: string | null | undefined
  deliveryData: PageDelivery | undefined
  deliveryLoading: boolean
  deliveryError: string | undefined
  onPreview: () => void
  draftActive?: boolean
}) {
  const publicAddress = productDomain
    ? `https://${productDomain.replace(/^https?:\/\//, "").replace(/\/$/, "")}${slug}`
    : null
  return (
    <section className="rounded-md border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={draftActive ? "default" : "outline"}
          onClick={onPreview}
          disabled={draftActive}
        >
          <Eye className="mr-1.5 h-4 w-4" /> Draft
        </Button>
        <DeliveryLink label="Staged" channel={deliveryData?.staging} loading={deliveryLoading} />
        <DeliveryLink label="Live" channel={deliveryData?.production} loading={deliveryLoading} />
        {publicAddress && !deliveryData?.production.available ? (
          <span className="text-[11px] text-muted-foreground">Public address: {publicAddress}</span>
        ) : null}
      </div>
      {deliveryError ? (
        <p className="mt-2 text-xs text-destructive">Release truth unavailable: {deliveryError}</p>
      ) : (
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Draft is the current AIX revision. Staged and Live enable only when that route exists in the active immutable
          release.
        </p>
      )}
    </section>
  )
}

function DeliveryLink({
  label,
  channel,
  loading,
}: {
  label: string
  channel: PageDelivery["staging"] | undefined
  loading: boolean
}) {
  if (channel?.available) {
    return (
      <Button size="sm" variant="outline" asChild>
        <a href={channel.url} target="_blank" rel="noreferrer">
          <ExternalLink className="mr-1.5 h-4 w-4" /> {label}
        </a>
      </Button>
    )
  }
  return (
    <Button size="sm" variant="outline" disabled title={channel?.reason ?? `${label} release unavailable`}>
      {loading ? "Checking…" : label}
    </Button>
  )
}

function PreviewMetadata({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt>{label}</dt>
      <dd className={`truncate text-foreground ${mono ? "font-mono" : ""}`} title={value}>
        {value}
      </dd>
    </div>
  )
}

function GuidedFindings({ page, onEdit }: { page: PageDetail; onEdit: () => void }) {
  const findings = page.gateResult?.findings ?? []
  const presentation = presentSeoFindings(findings)
  let statusLabel = "not checked"
  if (page.gateResult?.passed) {
    statusLabel = "passed"
  } else if (page.gateResult) {
    const warningLabel =
      presentation.warningCount > 0
        ? ` · ${presentation.warningCount} warning${presentation.warningCount === 1 ? "" : "s"}`
        : ""
    statusLabel = `${presentation.blockerCount} blocker${presentation.blockerCount === 1 ? "" : "s"}${warningLabel}`
  }
  const copyAgentRepairRequest = () => {
    const request = {
      task: "Propose a dry-run SEO page repair. Do not save, approve, gate, or publish.",
      slug: page.slug,
      expectedContentHash: page.contentHash,
      findings,
      requiredOutput: ["explanation", "fieldDiff", "evidenceIds", "complete proposed SeoPage JSON"],
    }
    void navigator.clipboard.writeText(JSON.stringify(request, null, 2))
    toast.success("Agent repair request copied")
  }
  return (
    <section className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Quality guidance</p>
          <p className="text-xs text-muted-foreground">
            The same findings are shown in plain language and technical detail.
          </p>
        </div>
        <Badge variant={!page.gateResult ? "secondary" : page.gateResult.passed ? "success" : "destructive"}>
          {statusLabel}
        </Badge>
      </div>
      {!page.gateResult ? (
        <p className="text-sm text-muted-foreground">Run Gate to generate a repair checklist.</p>
      ) : null}
      {presentation.findings.map((finding) => {
        const guidance = findingGuidance(finding.ruleId)
        return (
          <article key={`${finding.ruleId}-${finding.field ?? finding.message}`} className="rounded bg-muted p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{guidance.problem}</p>
                <p className="mt-1 text-xs text-muted-foreground">Why it matters: {guidance.impact}</p>
                <p className="mt-1 text-xs">Next: {guidance.nextAction}</p>
              </div>
              <Badge variant={finding.severity === "error" ? "destructive" : "outline"}>{finding.severity}</Badge>
            </div>
            <details className="mt-2 text-[11px] text-muted-foreground">
              <summary className="cursor-pointer">Technical evidence</summary>
              <p className="mt-1 font-mono">
                {finding.ruleId}
                {finding.field ? ` · ${finding.field}` : ""}
              </p>
              <p>{finding.message}</p>
            </details>
          </article>
        )
      })}
      {page.gateResult && !page.gateResult.passed ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="mr-1.5 h-4 w-4" /> Repair in editor
          </Button>
          <Button size="sm" variant="outline" onClick={copyAgentRepairRequest}>
            Copy agent repair request
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function findingGuidance(ruleId: string): { problem: string; impact: string; nextAction: string } {
  if (ruleId.startsWith("evidence."))
    return {
      problem: "This page needs stronger supporting evidence",
      impact: "Unsupported or stale claims can block publication and reduce trust.",
      nextAction: "Open the editor and attach a publishable source with reviewed rights.",
    }
  if (ruleId === "media.hero_required")
    return {
      problem: "The page hero image is missing",
      impact: "The page opens without a concrete visual example of the promised creative outcome.",
      nextAction: "Attach one owned or licensed hero image with provenance, dimensions, and descriptive alt text.",
    }
  if (ruleId === "media.example_required")
    return {
      problem: "This example needs its own output media",
      impact: "Readers can see the prompt and description but cannot inspect the corresponding result.",
      nextAction: "Attach the matching owned or licensed output to the example named in Technical evidence.",
    }
  if (ruleId.startsWith("media."))
    return {
      problem: "Required page media is incomplete",
      impact: "The page cannot demonstrate the prompt or creative outcome safely.",
      nextAction: "Attach owned or licensed media with provenance, dimensions, alt text, and the correct role.",
    }
  if (ruleId === "editorial.article_mismatch")
    return {
      problem: "Copy review found a possible a/an mismatch",
      impact: "This is an editorial warning, not a publication blocker, but it can make the page feel unpolished.",
      nextAction: "Review the nearby sentence in the editor and correct the article only if the warning is accurate.",
    }
  if (ruleId.startsWith("links."))
    return {
      problem: "Internal linking is incomplete",
      impact: "Readers and crawlers may not reach the related hub and sibling pages.",
      nextAction: "Add valid page paths in Internal links, then rerun Gate.",
    }
  if (ruleId.startsWith("metadata.") || ruleId.startsWith("intent."))
    return {
      problem: "Search-result messaging needs adjustment",
      impact: "The keyword, title, or description may be unclear or truncated in search.",
      nextAction: "Edit the Search result section and make the target intent explicit.",
    }
  if (ruleId.startsWith("privacy.") || ruleId.startsWith("safety."))
    return {
      problem: "Sensitive or unsafe content needs human review",
      impact: "Publishing may expose private data or violate content policy.",
      nextAction: "Remove the flagged material and request a human policy review before gating again.",
    }
  if (ruleId.startsWith("claims.") || ruleId.startsWith("trademark."))
    return {
      problem: "A claim needs evidence or safer wording",
      impact: "Unsubstantiated claims can create legal and credibility risk.",
      nextAction: "Add explicit evidence or rewrite the claim so it says only what can be supported.",
    }
  return {
    problem: "Page content does not meet the publication contract",
    impact: "The release gate will keep this draft out of a publishable release.",
    nextAction: "Open the editor, fix the field named in Technical evidence, and rerun Gate.",
  }
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-40 text-xs">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{label}: any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
      <p className={`text-sm ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</p>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      <div>Loading…</div>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return <div className="p-8 text-center text-sm text-muted-foreground">{message}</div>
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="m-4 rounded border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
      {message}
    </div>
  )
}
